import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Save, Loader2, Tag, ListPlus, Info, Wand2 } from "lucide-react";

const TERM_LABELS = {
  entity: "Customer noun (singular)",
  entity_plural: "Customer noun (plural)",
  identifier: "Account identifier",
  amount: "Amount column",
  nav_accounts: "Accounts menu label",
  account_noun: "Account noun",
};

export default function FieldSettings() {
  const { user } = useAuth();
  const noTenantSelected = user?.role === "super_admin" && !localStorage.getItem("active_tenant_id");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [core, setCore] = useState([]);
  const [termKeys, setTermKeys] = useState([]);
  const [labelOv, setLabelOv] = useState({});
  const [termOv, setTermOv] = useState({});
  const [terms, setTerms] = useState({});
  const [customs, setCustoms] = useState([]);
  const [types, setTypes] = useState([]);
  const [industryName, setIndustryName] = useState("");
  const [preset, setPreset] = useState({ terms: {}, custom_fields: [] });
  const [locks, setLocks] = useState({});
  const [termSources, setTermSources] = useState({});

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/tenant/field-config"), api.get("/tenant-config")])
      .then(([fc, tc]) => {
        setCore(fc.data.core_fields || []);
        setTermKeys(fc.data.term_keys || []);
        setLabelOv(fc.data.label_overrides || {});
        setTermOv(fc.data.term_overrides || {});
        setTypes(fc.data.field_types || ["text", "number", "date", "select"]);
        setIndustryName(fc.data.industry_name || "");
        setPreset(fc.data.industry_preset || { terms: {}, custom_fields: [] });
        setLocks(fc.data.locks || {});
        setTermSources(fc.data.term_sources || {});
        setCustoms((fc.data.tenant_custom_fields || []).map((c) => ({
          ...c, options: (c.options || []).join(", "),
          min: c.validation?.min ?? "", max: c.validation?.max ?? "", regex: c.validation?.regex ?? "",
        })));
        setTerms(tc.data.terms || {});
      })
      .catch(() => toast.error("Could not load field settings"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setLabel = (key, v) => setLabelOv((m) => ({ ...m, [key]: v }));
  const setTerm = (key, v) => setTermOv((m) => ({ ...m, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const clean_labels = Object.fromEntries(Object.entries(labelOv).filter(([, v]) => v && v.trim()));
      const clean_terms = Object.fromEntries(Object.entries(termOv).filter(([, v]) => v && v.trim()));
      const payload_customs = customs
        .filter((c) => (c.label || "").trim())
        .map((c) => {
          const out = { key: c.key || undefined, label: c.label.trim(), type: c.type };
          if (c.type === "select") out.options = (c.options || "").split(",").map((o) => o.trim()).filter(Boolean);
          const validation = {};
          if (c.type === "number") {
            if (c.min !== "" && c.min != null) validation.min = c.min;
            if (c.max !== "" && c.max != null) validation.max = c.max;
          } else if (c.type === "text" || c.type === "date") {
            if (c.regex) validation.regex = c.regex;
          }
          if (Object.keys(validation).length) out.validation = validation;
          return out;
        });
      await api.put("/tenant/field-config", { label_overrides: clean_labels, term_overrides: clean_terms, custom_fields: payload_customs });
      window.dispatchEvent(new Event("recoveros:config-changed"));
      toast.success("Field settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl flex items-center gap-2 text-muted-foreground py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="max-w-4xl" data-testid="field-settings-page">
      <PageHeader title="Field Settings" subtitle="Rename field labels to match your terminology. Field types, custom fields and locks are managed centrally by your platform administrator.">
        <Button onClick={save} disabled={saving || noTenantSelected} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="field-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </Button>
      </PageHeader>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/50 border border-border rounded-sm px-3 py-2 mb-5">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-[#0B3D91]" />
        You can rename labels only. Core financial fields (marked <Lock className="inline h-3 w-3" />) keep their meaning, and fields marked <Lock className="inline h-3 w-3 text-[#0B3D91]" /> are locked by your administrator. Renaming is display-only — Dashboard, Money Stuck Analyzer and aging buckets are never affected.
      </div>

      {noTenantSelected && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5 mb-5" data-testid="no-tenant-banner">
          <Info className="h-4 w-4 shrink-0" />
          Select a specific tenant from the top bar to edit and save its field labels.
        </div>
      )}

      {/* Terminology */}
      <Section icon={Tag} title="Terminology" testId="field-terms-section">
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {termKeys.map((k) => (
            <Row key={k} label={<span className="inline-flex items-center gap-2">{locks[k] && <Lock className="h-3 w-3 text-[#0B3D91]" />}{TERM_LABELS[k] || k}<SourceBadge source={termSources[k]} /></span>}>
              <Input value={termOv[k] ?? ""} placeholder={terms[k] || ""} disabled={!!locks[k]} onChange={(e) => setTerm(k, e.target.value)}
                className="rounded-sm h-9" data-testid={`term-${k}`} />
            </Row>
          ))}
        </div>
      </Section>

      {/* Standard field labels */}
      <Section icon={Tag} title="Standard Field Labels" testId="field-core-section">
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {core.map((f) => (
            <Row key={f.key} label={<span className="inline-flex items-center gap-2">{f.locked && <Lock className="h-3 w-3 text-amber-600" />}{f.tenant_locked && <Lock className="h-3 w-3 text-[#0B3D91]" />}{f.label}<SourceBadge source={f.source} /></span>}>
              <Input value={labelOv[f.key] ?? ""} placeholder={f.label} disabled={!!f.tenant_locked} onChange={(e) => setLabel(f.key, e.target.value)}
                className="rounded-sm h-9" data-testid={`label-${f.key}`} />
            </Row>
          ))}
        </div>
      </Section>

      <div className="flex justify-end mt-2">
        <Button onClick={save} disabled={saving || noTenantSelected} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="field-save-btn-bottom">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </Button>
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    default: ["Default", "bg-slate-100 text-slate-600"],
    admin: ["Admin", "bg-[#0B3D91]/10 text-[#0B3D91]"],
    tenant: ["Custom", "bg-emerald-100 text-emerald-700"],
  };
  const [txt, cls] = map[source] || map.default;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`} data-testid={`source-${source || "default"}`}>{txt}</span>;
}

function Section({ icon: Icon, title, action, children, testId }) {
  return (
    <div className="border border-border bg-card p-5 mb-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-heading font-bold"><Icon className="h-4 w-4 text-[#0B3D91]" /> {title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}
